import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Sparkles, Share2, Users, Lock, ShieldCheck, Link2, Heart, MessageCircle, LineChart, Gift, Trophy } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { AuraRing } from "@/components/brand/AuraRing";
import { CoinIcon } from "@/components/app/AuramaxChip";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl, UPDATE_VERSION } from "@/lib/api";
import { cn } from "@/lib/utils";

/* ---------- slide visuals (icons / SVG / CSS only) ---------- */
function IntroVisual({ still }) {
  return (
    <div className="relative grid h-44 place-items-center">
      <motion.div className="absolute h-40 w-40 rounded-full aura-radial blur-2xl" initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 0.6, scale: 1 }} transition={{ duration: still ? 0 : 0.8 }} />
      <motion.div initial={{ scale: still ? 1 : 0.5, opacity: still ? 1 : 0, rotate: still ? 0 : -12 }} animate={{ scale: 1, opacity: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 160, damping: 14 }}><Logo size={96} className="relative" /></motion.div>
    </div>
  );
}
function CoinVisual({ still }) {
  return (
    <div className="grid h-44 place-items-center">
      <motion.div initial={{ rotateY: still ? 0 : 180, opacity: still ? 1 : 0 }} animate={{ rotateY: 0, opacity: 1 }} transition={{ duration: 0.7 }} className="grid h-24 w-24 place-items-center rounded-full border-2 border-[#d4a84b]/60 bg-[#d4a84b]/10 text-[#e5c067] shadow-[0_0_60px_rgba(212,168,75,0.25)]"><CoinIcon size={54} /></motion.div>
      <div className="mt-4 font-display text-2xl font-extrabold text-white">Your <span className="text-[#e5c067]">$AURAMAX</span>, live from Solana</div>
    </div>
  );
}
function ForYouVisual({ still }) {
  return (
    <div className="relative grid h-44 place-items-center">
      {[2, 1, 0].map((i) => (
        <motion.div key={`card${i}`} initial={{ y: still ? i * -10 : 60, opacity: still ? 1 : 0, scale: 1 - i * 0.06 }} animate={{ y: i * -10, opacity: 1, scale: 1 - i * 0.06 }} transition={{ delay: still ? 0 : 0.1 * (2 - i), duration: 0.4 }}
          className="absolute h-28 w-56 rounded-2xl border border-am-border bg-am-surface2 p-3 shadow-xl" style={{ zIndex: 3 - i }}>
          <div className="flex items-center gap-2"><span className="h-6 w-6 rounded-full aura-gradient" /><span className="h-2 w-20 rounded bg-white/20" /></div>
          <span className="mt-2 block h-2 w-40 rounded bg-white/10" /><span className="mt-1 block h-2 w-28 rounded bg-white/10" />
          <div className="mt-3 flex gap-3 text-am-text3"><Heart className="h-3.5 w-3.5" /><MessageCircle className="h-3.5 w-3.5" /></div>
        </motion.div>
      ))}
    </div>
  );
}
function ShareVisual({ still, user }) {
  return (
    <div className="grid h-44 place-items-center">
      <motion.div initial={{ scale: still ? 1 : 0.85, opacity: still ? 1 : 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }} className="w-64 rounded-2xl border border-am-border bg-am-surface2 p-3">
        <div className="h-10 rounded-xl aura-gradient opacity-40" />
        <div className="-mt-5 ml-2 flex items-end gap-2"><AuraRing src={mediaUrl(user?.picture) || undefined} size={40} ring={2} fallback={user?.name} /><div className="pb-1 text-xs"><div className="font-bold text-white">{user?.name}</div><div className="text-am-text2">@{user?.username}</div></div></div>
        <motion.div initial={{ opacity: still ? 1 : 0, y: still ? 0 : 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: still ? 0 : 0.35 }} className="mt-3 flex items-center gap-1.5 rounded-full border border-am-border bg-am-bg px-2.5 py-1 text-[11px] text-aura-cyan"><Link2 className="h-3 w-3" /> auramax.dev/@{user?.username}</motion.div>
      </motion.div>
    </div>
  );
}
function InviteVisual({ still, user }) {
  const pts = [[-70, -30], [70, -30], [-70, 40], [70, 40], [0, -60]];
  return (
    <div className="relative grid h-44 place-items-center">
      <svg className="absolute h-full w-64" viewBox="-100 -80 200 160">{pts.map(([x, y], i) => <motion.line key={`l${x},${y}`} x1="0" y1="0" x2={x} y2={y} stroke="url(#g)" strokeWidth="1.2" initial={{ pathLength: still ? 1 : 0 }} animate={{ pathLength: 1 }} transition={{ delay: still ? 0 : 0.1 * i, duration: 0.5 }} />)}<defs><linearGradient id="g"><stop offset="0" stopColor="#ff2e93" /><stop offset="1" stopColor="#38e1ff" /></linearGradient></defs></svg>
      <div className="relative z-10"><AuraRing src={mediaUrl(user?.picture) || undefined} size={56} ring={2.5} fallback={user?.name} /></div>
      {pts.map(([x, y], i) => <motion.span key={`n${x},${y}`} initial={{ scale: still ? 1 : 0 }} animate={{ scale: 1 }} transition={{ delay: still ? 0 : 0.15 * i + 0.2, type: "spring" }} className="absolute grid h-9 w-9 place-items-center rounded-full border border-am-border bg-am-surface2 text-am-text2" style={{ transform: `translate(${x * 1.3}px, ${y * 1.1}px)` }}><Users className="h-4 w-4" /></motion.span>)}
    </div>
  );
}
function MarketVisual({ still }) {
  const tiles = [["chart", LineChart], ["gift", Gift], ["trophy", Trophy]];
  return (
    <div className="grid h-44 place-items-center">
      <div className="grid grid-cols-3 gap-2">{tiles.map(([k, I], i) => <motion.div key={k} initial={{ y: still ? 0 : 20, opacity: still ? 1 : 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: still ? 0 : 0.12 * i }} className="relative grid h-24 w-20 place-items-center rounded-2xl border border-am-border bg-am-surface2 text-am-text2"><I className="h-6 w-6" /><span className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full aura-gradient"><Lock className="h-3 w-3 text-black" /></span></motion.div>)}</div>
    </div>
  );
}
function SafetyVisual({ still }) {
  return (
    <div className="grid h-44 place-items-center">
      <motion.div initial={{ scale: still ? 1 : 0.7, opacity: still ? 1 : 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 180 }} className="grid h-20 w-20 place-items-center rounded-3xl border border-aura-mint/40 bg-aura-mint/10 text-aura-mint"><ShieldCheck className="h-10 w-10" /></motion.div>
      <div className="mt-4 w-56"><div className="mb-1 flex justify-between text-[11px] text-am-text2"><span>Profile</span><span>100%</span></div><div className="h-2 overflow-hidden rounded-full bg-am-surface3"><motion.div initial={{ width: still ? "100%" : "20%" }} animate={{ width: "100%" }} transition={{ duration: 0.9, delay: still ? 0 : 0.3 }} className="h-full rounded-full aura-gradient" /></div></div>
    </div>
  );
}
const CONFETTI = Array.from({ length: 18 }, (_, i) => i);
function FinishVisual({ still, user }) {
  const colors = ["#ff2e93", "#ff7a3c", "#ffd23c", "#37e6b0", "#38e1ff", "#5b8cff"];
  return (
    <div className="relative grid h-44 place-items-center overflow-hidden">
      {!still && CONFETTI.map((i) => <motion.span key={`c${i}`} initial={{ y: -20, x: (i - 9) * 16, opacity: 1, rotate: 0 }} animate={{ y: 190, rotate: 360 + i * 20, opacity: 0 }} transition={{ duration: 1.6 + (i % 4) * 0.3, delay: (i % 6) * 0.08, ease: "easeIn" }} className="absolute top-0 h-2.5 w-1.5 rounded-sm" style={{ background: colors[i % colors.length] }} />)}
      <motion.div initial={{ scale: still ? 1 : 0.6 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 150 }}><AuraRing src={mediaUrl(user?.picture) || undefined} size={96} ring={3} fallback={user?.name} /></motion.div>
    </div>
  );
}

export const SLIDES = [
  { key: "intro", Visual: IntroVisual, title: "AuraMax just leveled up", body: "A quick tour of what's new. It takes 30 seconds." },
  { key: "coin", Visual: CoinVisual, title: "Your $AURAMAX balance", body: "Link your Solana wallet on the Balance screen to see your real SOL and $AURAMAX holdings. Read-only, private to you." },
  { key: "foryou", Visual: ForYouVisual, title: "A smarter For You", body: "For You is now your home. Fresh posts, new creators boosted, and it never runs empty." },
  { key: "share", Visual: ShareVisual, title: "Share your profile anywhere", body: "Your profile has a clean link with rich previews in iMessage, Instagram, X and Discord." },
  { key: "invite", Visual: InviteVisual, title: "Invite your people", body: "Your username is your referral code. Friends who join through your link follow you automatically." },
  { key: "market", Visual: MarketVisual, title: "Market, taking shape", body: "Top referrers plus a preview of what's coming: live chart, giveaways, challenges, IRL events and more." },
  { key: "safety", Visual: SafetyVisual, title: "Safer, more complete", body: "Report or block from any “…” menu, and a profile completion meter helps you finish your setup." },
  { key: "finish", Visual: FinishVisual, title: "You're all set", body: "Everything you had is still here — Aura, posts, follows, DMs. Go max your aura." },
];

const variants = { enter: (d) => ({ x: d > 0 ? 60 : -60, opacity: 0 }), center: { x: 0, opacity: 1 }, exit: (d) => ({ x: d > 0 ? -60 : 60, opacity: 0 }) };

export function WhatsNew({ forceOpen = false, onClose }) {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(forceOpen);
  const [[i, dir], setI] = useState([0, 1]);
  const [pct, setPct] = useState(100);
  const touch = useRef(null);

  useEffect(() => {
    if (forceOpen) { setOpen(true); return; }
    if (!user) return;
    if (user.onboarding_complete === false) return;
    if (user.last_seen_update_version !== UPDATE_VERSION) { const t = setTimeout(() => setOpen(true), 600); return () => clearTimeout(t); }
  }, [user, forceOpen]);
  useEffect(() => { if (open) api.completion().then((c) => setPct(c.pct)).catch(() => {}); }, [open]);

  const close = useCallback(async (to) => {
    setOpen(false);
    try { await api.whatsNewSeen(); await checkAuth(); } catch {}
    onClose && onClose();
    if (to) navigate(to);
  }, [checkAuth, navigate, onClose]);

  const go = useCallback((d) => setI(([c]) => [Math.min(SLIDES.length - 1, Math.max(0, c + d)), d]), []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "ArrowRight") go(1); else if (e.key === "ArrowLeft") go(-1); else if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, [open, go, close]);

  if (!open) return null;
  const s = SLIDES[i];
  const last = i === SLIDES.length - 1;
  const dur = reduce ? 0 : 0.35;

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-am-bg/95 p-4 backdrop-blur-xl" data-testid="whats-new"
      onTouchStart={(e) => (touch.current = e.touches[0].clientX)} onTouchEnd={(e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); touch.current = null; }}>
      <button onClick={() => close()} data-testid="whats-new-skip" className="absolute right-4 top-4 flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold text-am-text2 hover:bg-white/5 hover:text-white">Skip <X className="h-4 w-4" /></button>
      <div className="w-full max-w-md">
        <div className="relative min-h-[380px] overflow-hidden">
          <AnimatePresence custom={dir} mode="wait" initial={false}>
            <motion.div key={s.key} custom={dir} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: dur, ease: "easeOut" }} className="text-center" data-testid={`whats-new-slide-${s.key}`}>
              <s.Visual still={!!reduce} user={user} />
              <h2 className="mt-4 font-display text-2xl font-extrabold text-white sm:text-3xl">{s.title}</h2>
              <p className="mx-auto mt-2 max-w-sm text-sm text-am-text2 md:text-base">{s.body}</p>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="mt-6 flex items-center justify-center gap-1.5" data-testid="whats-new-dots">
          {SLIDES.map((sl, k) => <button key={sl.key} onClick={() => setI([k, k > i ? 1 : -1])} className={cn("h-1.5 rounded-full transition-all", k === i ? "w-6 aura-gradient" : "w-1.5 bg-white/20")} aria-label={`Slide ${k + 1}`} />)}
        </div>
        <div className="mt-6 flex items-center gap-2">
          {i > 0 && <button onClick={() => go(-1)} data-testid="whats-new-back" className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-am-border text-white hover:bg-white/5"><ChevronLeft className="h-5 w-5" /></button>}
          {!last ? (
            <button onClick={() => go(1)} data-testid="whats-new-next" className="flex flex-1 items-center justify-center gap-2 rounded-full aura-gradient py-3.5 text-sm font-bold text-black">Next <ChevronRight className="h-4 w-4" /></button>
          ) : (
            <div className="flex flex-1 flex-col gap-2">
              {pct < 100 && <button onClick={() => close("/u/me")} data-testid="whats-new-complete-profile" className="flex items-center justify-center gap-2 rounded-full border border-am-border-strong py-3 text-sm font-semibold text-white hover:bg-white/5"><Sparkles className="h-4 w-4 aura-text" /> Complete my profile</button>}
              <button onClick={() => close()} data-testid="whats-new-done" className="flex items-center justify-center gap-2 rounded-full aura-gradient py-3.5 text-sm font-bold text-black">Let's go <Share2 className="hidden" /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
